/* eslint-disable import/prefer-default-export */

import { MAX_QUESTIONNAIRES_PER_CREATOR, MIN_QUESTIONNAIRES_PER_CREATOR } from './rxdb.constants';

export type CompletionPromiseReturn = {
  promise: Promise<void>;
  resolver: (value: void | Promise<void>) => void;
};

/**
 * This function returns a promise that will resolve when the resolver is called.
 */
export function getPromiseAndResolver(): CompletionPromiseReturn {
  let wasCalled = false;
  let resolver: (value: void | Promise<void>) => void = () => {
    wasCalled = true;
  };
  const promise = new Promise<void>(r => {
    if (wasCalled) {
      r();
      return;
    }

    resolver = r;
  });
  return { promise, resolver };
}

export function rxdbLog(message: string, ...args: unknown[]): void {
  // eslint-disable-next-line no-console
  console.log(`%c[RxDB] ${message}`, 'color: #0570eb; font-weight: bold', ...args);
}

export function rxdbError(message: string, ...args: unknown[]): void {
  console.error('[RxDB]', message, ...args);
}

export async function wait(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * Returns the available space in MB for the current domain.
 *
 * Returns -1 if the browser does not support the storage API.
 */
export async function getAvailableSpaceInMB(): Promise<number> {
  if (!('storage' in navigator) || !('estimate' in navigator.storage)) return -1;

  // Estimate() method returns information about quota and used space:
  const { usage, quota } = await navigator.storage.estimate();

  if (!usage || !quota) return -1;

  // Calculate available space.
  const availableSpaceInBytes = quota - usage;

  // Convert to megabytes.
  // There are 1024 bytes in a kilobyte and 1024 kilobytes in a megabyte
  const availableSpaceinMB = availableSpaceInBytes / (1024 * 1024);

  return availableSpaceinMB;
}

/**
 * Returns the maximum number of questionnaires allowed per creator based on available storage space.
 *
 * @returns {Promise<number>} The maximum number of questionnaires allowed
 */
export async function getMaxQuestionnairesPerCreator(): Promise<number> {
  const availableSpace = await getAvailableSpaceInMB();

  // If we can't determine available space, return the minimum
  if (availableSpace === -1) {
    return MIN_QUESTIONNAIRES_PER_CREATOR;
  }

  // Calculate maximum space we want to use (30% of available space)
  const maxAllowedSpace = availableSpace * 0.3;

  // Use the maximum size per questionnaire (60MB) to be conservative
  const maxSizePerQuestionnaire = 60;

  // Calculate how many questionnaires can fit in the allowed space
  const maxQuestionnaires = Math.floor(maxAllowedSpace / maxSizePerQuestionnaire);

  // Ensure the result is within our min/max bounds
  return Math.min(
    Math.max(maxQuestionnaires, MIN_QUESTIONNAIRES_PER_CREATOR),
    MAX_QUESTIONNAIRES_PER_CREATOR,
  );
}
